// Brands.jsx
import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Input, message, Space, Alert } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TagOutlined,
} from "@ant-design/icons";
import BrandsService from "./BrandsService";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import RowActionsMenu from "../components/RowActionsMenu";

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);

  const [form] = Form.useForm();
  const { user } = useAuth();
  const { selectedCompany } = useSelection();

  // Helper function for permission checks
  const hasPermission = (permissionKey) => {
    return user?.permissions?.includes(permissionKey) || false;
  };

  // Charger les données
  const fetchBrands = async () => {
    if (!selectedCompany) {
      setBrands([]);
      return;
    }

    setLoading(true);
    try {
      const response = await BrandsService.getAll(selectedCompany);
      setBrands(response.data);
    } catch (error) {
      message.error("Erreur lors de la récupération des marques");
      console.error("fetchBrands() error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, [selectedCompany]);

  // Ouvrir la modal (création ou édition)
  const showModal = (brand = null) => {
    if (!selectedCompany) {
      message.warning("Veuillez sélectionner une entreprise");
      return;
    }

    setEditingBrand(brand);
    form.resetFields();

    if (brand) {
      form.setFieldsValue({
        name: brand.name,
        description: brand.description,
        slug: brand.slug,
      });
    } else {
      form.setFieldsValue({
        name: "",
        description: "",
        slug: "",
      });
    }
    setIsModalVisible(true);
  };

  // Fermer la modal
  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingBrand(null);
  };

  // Génération automatique du slug en fonction du nom
  const handleNameChange = (e) => {
    const newName = e.target.value;
    const newSlug = newName
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "");
    form.setFieldsValue({ name: newName, slug: newSlug });
  };

  // Suppression
  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer cette marque ?",
      content: "Cette action pourrait affecter les produits associés.",
      okText: "Oui, Supprimer",
      okType: "danger",
      cancelText: "Non",
      centered: true,
      onOk: async () => {
        try {
          await BrandsService.delete(id);
          message.success("Marque supprimée avec succès");
          fetchBrands();
        } catch (error) {
          if (
            error.response &&
            error.response.status === 409 &&
            error.response.data?.error
          ) {
            message.error(error.response.data.error);
          } else {
            message.error("Erreur lors de la suppression de la marque.");
            console.error(
              "[handleDelete onOk] error:",
              error.response || error
            );
          }
        }
      },
    });
  };

  // Création ou mise à jour
  const handleSubmit = async (values) => {
    if (!selectedCompany) {
      message.error("Aucune entreprise sélectionnée");
      return;
    }

    try {
      const dataWithCompany = {
        ...values,
        company_id: selectedCompany,
      };

      if (editingBrand) {
        await BrandsService.update(editingBrand.id, dataWithCompany);
        message.success("Marque mise à jour avec succès");
      } else {
        await BrandsService.create(dataWithCompany);
        message.success("Marque créée avec succès");
      }
      setIsModalVisible(false);
      fetchBrands();
    } catch (error) {
      if (error.response && error.response.status === 409) {
        message.error(
          error.response.data?.error || "Cette marque existe déjà !"
        );
      } else if (error.response && error.response.status === 400) {
        message.error(error.response.data?.error || "Données invalides");
      } else {
        message.error("Erreur lors de la sauvegarde de la marque");
      }
      console.error("handleSubmit() error:", error);
    }
  };

  // Colonnes du tableau
  const columns = [
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      responsive: ["md"],
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 72,
      align: "center",
      fixed: "right",
      render: (_, record) => {
        const actionItems = [];

        if (hasPermission("Gestion Commerciale.Produits.Marques.edit")) {
          actionItems.push({
            key: "edit",
            icon: <EditOutlined />,
            label: "Modifier",
            onClick: () => showModal(record),
          });
        }

        if (hasPermission("Gestion Commerciale.Produits.Marques.delete")) {
          if (actionItems.length > 0) actionItems.push({ type: "divider" });
          actionItems.push({
            key: "delete",
            icon: <DeleteOutlined />,
            label: "Supprimer",
            danger: true,
            onClick: () => handleDelete(record.id),
          });
        }

        return (
          <RowActionsMenu
            items={actionItems}
            ariaLabel={`Afficher les actions de la marque ${record.name || record.id}`}
          />
        );
      },
    },
  ];

  // Si aucune entreprise n'est sélectionnée
  if (!selectedCompany) {
    return (
      <div
        className="p-sm md:p-md lg:p-lg min-h-screen"
        style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)" }}
      >
        <div className="mb-lg p-md shadow rounded-lg" style={{ backgroundColor: "var(--bg-primary)" }}>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold flex items-center" style={{ color: "var(--text-primary)" }}>
            <TagOutlined className="mr-3" /> Gestion des Marques
          </h1>
          <Alert
            message="Aucune entreprise sélectionnée"
            description="Veuillez sélectionner une entreprise dans le menu de droite pour gérer les marques."
            type="warning"
            showIcon
            className="mt-4"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="p-sm md:p-md lg:p-lg min-h-screen"
      style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)" }}
    >
      <div className="mb-lg p-md shadow rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between" style={{ backgroundColor: "var(--bg-primary)" }}>
        <div className="mb-sm sm:mb-0">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold flex items-center" style={{ color: "var(--text-primary)" }}>
            <TagOutlined className="mr-3" /> Gestion des Marques
          </h1>
          <p className="mt-1 text-xs sm:text-sm" style={{ color: "var(--text-secondary)" }}>
            Administrez les marques de vos produits.
          </p>
        </div>
        {hasPermission("Gestion Commerciale.Produits.Marques.create") && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
            className="w-full sm:w-auto"
          >
            Ajouter une marque
          </Button>
        )}
      </div>

      <div className="overflow-x-auto shadow rounded-lg" style={{ backgroundColor: "var(--bg-primary)" }}>
        <Table
          dataSource={brands}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          className="w-full min-w-[600px]"
          scroll={{ x: "max-content" }}
          locale={{
            emptyText: selectedCompany
              ? "Aucune marque trouvée pour cette entreprise"
              : "Veuillez sélectionner une entreprise",
          }}
        />
      </div>

      <Modal
        title={
          <div className="flex items-center" style={{ color: "var(--text-primary)" }}>
            <TagOutlined className="mr-2" />
            {editingBrand ? "Modifier la Marque" : "Ajouter une Marque"}
          </div>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        centered
      >
        <div
          className="modal-body p-sm sm:p-md rounded-b-lg"
          style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)" }}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="name"
              label="Nom de la marque"
              rules={[
                {
                  required: true,
                  message: "Veuillez saisir le nom de la marque",
                },
              ]}
            >
              <Input
                onChange={handleNameChange}
                placeholder="Ex: SuperMarque"
              />
            </Form.Item>

            <Form.Item name="slug" label="Slug (auto-généré)">
              <Input readOnly placeholder="sera-genere-automatiquement" />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea
                rows={3}
                placeholder="Brève description de la marque (optionnel)"
              />
            </Form.Item>

            <Form.Item className="mt-md pt-md border-t" style={{ borderColor: "var(--border-color)" }}>
              <Space className="flex justify-end w-full">
                <Button onClick={handleCancel}>
                  Annuler
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingBrand ? "Mettre à jour" : "Créer"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default Brands;
